import { FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SearchResult } from '@localagent/sdk';
import { getSDK } from '../../services/sdk-instance';

export function FeedSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length === 0) {
      setHits([]);
      setError(null);
      return;
    }
    let cancelled = false;
    const handle = window.setTimeout(() => {
      setPending(true);
      void getSDK()
        .search.search({ query: q, entity_types: ['feed_post'] })
        .then((resp) => {
          if (!cancelled) {
            setHits(resp.results);
            setError(null);
          }
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : String(err));
          }
        })
        .finally(() => {
          if (!cancelled) setPending(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query]);

  function openHit(hit: SearchResult) {
    navigate(`/feed/${hit.entity_id}`);
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const first = hits[0];
    if (first) openHit(first);
  }

  return (
    <form data-testid="feed-search" className="space-y-2" onSubmit={handleSubmit}>
      <input
        data-testid="feed-search-input"
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        className="w-full rounded-md bg-muted px-3 py-2 text-body"
        placeholder="Search posts…"
        aria-label="Search Live Feed posts"
      />
      {error ? (
        <p data-testid="feed-search-error" className="text-body text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      {pending && hits.length === 0 ? (
        <p className="text-caption text-muted-foreground">Searching…</p>
      ) : null}
      {hits.length > 0 ? (
        <ul data-testid="feed-search-results" className="space-y-1">
          {hits.map((hit) => (
            <li key={hit.entity_id}>
              <button
                type="button"
                data-testid="feed-search-hit"
                className="w-full rounded-md px-3 py-2 text-left text-body hover:bg-accent/50"
                onClick={() => openHit(hit)}
              >
                {hit.title || hit.snippet}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </form>
  );
}
